import React, { useCallback, useEffect, useRef, useState } from "react";
import "./StudentList.scss";
import { adminRequest } from "../../../utils/requestMethods";
import { BASE_URL } from "../../../utils/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotFound from "../../../components/NotFound/NotFound";
import { useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import { IoIosAddCircle, IoIosMore } from "react-icons/io";
import CustomForm from "../../../components/CustomForm/CustomForm";
import OptionsMenu from "../../../components/OptionsMenu/OptionsMenu";
import NoRecord from "../../../components/NoRecord/NoRecord";
import { CiSearch } from "react-icons/ci";
import { BsFiletypeCsv } from "react-icons/bs";
import { IoDownloadOutline } from "react-icons/io5";
import _ from "lodash";
import Swal from "sweetalert2";

const StudentList = () => {
  const [rows, setRows] = useState([]);
  const headers = [
    "Full Name",
    "Program",
    "Semester",
    "Section",
    "Email",
    "Phone Number",
    "Status",
  ];
  const initialFormData = {
    enrolledProgram: "",
    enrolledSemester: "",
    section: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [program, setProgram] = useState([]);
  const [semester, setSemester] = useState([]);
  const navigate = useNavigate();
  const [visibleMenu, setVisibleMenu] = useState(null);
  const menuRefs = useRef([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [promotionSemester, setPromotionSemester] = useState(null);
  const [assignSection, setAssignSection] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handlePromoteClick = () => {
    if (selectedStudents.length === 0) {
      return;
    }
    setIsDialogOpen(true);
  };
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleAssignClick = () => {
    if (selectedStudents.length === 0) {
      return;
    }
    setIsAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setIsAssignDialogOpen(false);
  };

  const handleDialogSubmit = async () => {
    if (!promotionSemester) {
      return;
    }
    try {
      const response = await toast.promise(
        adminRequest.post(`${BASE_URL}/student/promote`, {
          toSemester: promotionSemester,
          studentUuid: selectedStudents,
        }),
        { pending: "Processing your reques" },
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message);
    }
  };

  const handleAssignSection = async () => {
    if (!assignSection) {
      return;
    }
    try {
      const response = await toast.promise(
        adminRequest.post(`${BASE_URL}/section/student/assign`, {
          curriculumSectionId: assignSection,
          studentUuids: selectedStudents,
        }),
        { pending: "Processing your request" },
      );
      Swal.fire({
        title: "SUCCESS",
        icon: "success",
        confirmButtonColor: "#264796",
        text: `${response.data.message}`,
        confirmButtonText: "OK",
      });
    } catch (error) {
      Swal.fire({
        title: "SECTION ASSIGNMENT FAILED",
        icon: "error",
        confirmButtonColor: "#264796",
        text: `${error.response.data.message}`,
        confirmButtonText: "OK",
      });
    }
  };

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setProgram(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    if (formData.enrolledProgram) {
      const fetchSemester = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/getSemestersById`,
            {
              id: formData.enrolledProgram,
            },
          );
          setSemester(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSemester();
    }
    fetchProgram();
  }, [formData.enrolledProgram]);

  const handleExportToCSV = async () => {
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/student/export`,
        {
          enrolledProgram: formData?.enrolledProgram,
          enrolledSemester: formData?.enrolledSemester,
        },
        {
          responseType: "blob",
        },
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "students.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV", error);
    }
  };

  const fetchData = useCallback(async () => {
    const firstRow = (currentPage - 1) * pageSize;
    try {
      const filters = {
        enrolledProgram: formData.enrolledProgram || "",
        enrolledSemester: formData.enrolledSemester || "",
        section: formData.section || "",
        status: statusFilter || "",
        pageSize,
        firstRow,
      };

      const response = await adminRequest.post(
        `${BASE_URL}/student/list`,
        filters,
      );

      const { data, totalCount } = response.data;

      const fetchedRows = data.map((e) => ({
        displayData: [
          e.fullName,
          e.programName,
          e.semesterName,
          e.sections[0],
          e.email,
          e.phoneNumber,
          e.status,
        ],
        uuid: e.uuid,
        status: e.status,
      }));

      setRows(fetchedRows);
      setTotalCount(totalCount);
    } catch (error) {
      console.error("Failed to fetch students:", error);
      return <NotFound />;
    }
  }, [
    formData.enrolledProgram,
    formData.enrolledSemester,
    formData.section,
    currentPage,
    pageSize,
    statusFilter,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (formData.enrolledSemester) {
      const fetchSection = async () => {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/section/list/by/curriculum/uuid`,
            {
              uuid: formData.enrolledSemester,
            },
          );
          setSections(response.data.data);
        } catch (error) {
          console.log(error);
        }
      };
      fetchSection();
    }
  }, [formData.enrolledSemester]);

  const handleCreateButtonClick = () => {
    navigate("/student/create");
  };
  const handleImportButtonClick = () => {
    navigate("/student/import");
  };

  const handleMoreClick = (rowIndex) => {
    setVisibleMenu(visibleMenu === rowIndex ? null : rowIndex);
  };
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedStudents(rows.map((student) => student.uuid));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentUuid, checked) => {
    if (checked) {
      setSelectedStudents((prev) => [...prev, studentUuid]);
    } else {
      setSelectedStudents((prev) =>
        prev.filter((uuid) => uuid !== studentUuid),
      );
      setSelectAll(false);
    }
  };

  const handleSearch = React.useRef(null);

  useEffect(() => {
    handleSearch.current = _.debounce(async (query) => {
      if (!query) {
        fetchData();
        return;
      }
      try {
        const response = await adminRequest.post(`${BASE_URL}/student/search`, {
          searchKeyword: query,
        });
        const fetchedRows = response.data.data.map((e) => ({
          displayData: [
            e.fullName,
            e.programName,
            e.semesterName,
            e.sections[0],
            e.email,
            e.phoneNumber,
            e.status,
          ],
          uuid: e.uuid,
          status: e.status,
        }));
        setRows(fetchedRows);
      } catch (error) {
        console.error(error);
      }
    }, 300);
  }, [fetchData]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    if (handleSearch.current) {
      handleSearch.current(value);
    }
  };

  const filterFields = [
    {
      name: "enrolledProgram",
      label: "Enrolled Program",
      type: "select",
      value: formData.enrolledProgram,
      onChange: handleChange,
      options: [
        { label: "Select Program", value: "" },
        ...program.map((p) => ({
          label: p.short_name,
          value: p.id,
        })),
      ],
    },
    {
      name: "enrolledSemester",
      label: "Semester",
      type: "select",
      value: formData.enrolledSemester,
      onChange: handleChange,
      options: [
        { label: "Select Semester", value: "" },
        ...semester.map((s) => ({
          label: s.semester,
          value: s.uuid,
        })),
      ],
    },
    {
      name: "section",
      label: "Section",
      type: "select",
      value: formData.section,
      onChange: handleChange,
      options: [
        { label: "Select section", value: "" },
        ...sections.map((s) => ({
          label: s.Section.name,
          value: s.Section.uuid,
        })),
      ],
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      value: statusFilter,
      onChange: (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
      },
      options: [
        { label: "All Status", value: "" },
        { label: "Pending", value: "PENDING" },
        { label: "Active", value: "ACTIVE" },
        { label: "Graduated", value: "GRADUATED" },
        { label: "Suspended", value: "SUSPENDED" },
        { label: "Withdrawn", value: "WITHDRAWN" },
      ],
    },
  ];
  const getMenuItems = (row) => [
    { link: `view/${row.uuid}`, text: "View" },
    { link: `edit/${row.uuid}`, text: "Edit" },
  ];

  const startRecord = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="studentListContainer">
      <div className="studentListContents">
        <div className="top">
          <span className="leftTitle">
            <FaArrowLeftLong onClick={handleBackClick} className="icon" />
            Student List
          </span>
          <span className="rightContents">
            <span className="searchField">
              <input
                type="text"
                name="search"
                placeholder="Search Students..."
                className="searchInput"
                value={searchKeyword}
                onChange={handleInputChange}
              />
              <span className="searchIcon">
                <CiSearch />
              </span>
            </span>
            <span className="importButton" onClick={handleImportButtonClick}>
              <span className="title">Import CSV</span>
              <span className="csvIcon">
                <BsFiletypeCsv />
              </span>
            </span>
            <span className="exportButton" onClick={handleExportToCSV}>
              <span className="title">Download</span>
              <span className="csvIcon">
                <IoDownloadOutline />
              </span>
            </span>
            <span className="createButton" onClick={handleCreateButtonClick}>
              <span className="title">Create Student</span>
              <span className="addIcon">
                <IoIosAddCircle />
              </span>
            </span>
          </span>
        </div>
        <div className="filterForm">
          <CustomForm
            createButtonLabel="Search"
            flexDirection="row"
            showDefaultHeader={false}
            fields={filterFields}
            showCancelButton={false}
            showCloseButton={false}
            showCreateButton={false}
          />
        </div>
        <div className="bottom">
          <div className="recordSummary">
            <select
              className="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </div>
          <table className="listTable">
            <thead>
              <tr className="tableHeaderRow">
                <th></th>
                <th>
                  {formData.enrolledSemester && (
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="checkBox"
                    />
                  )}
                </th>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{ position: "relative" }}
                      ref={(el) => (menuRefs.current[rowIndex] = el)}
                    >
                      <IoIosMore onClick={() => handleMoreClick(rowIndex)} />
                      {visibleMenu === rowIndex && (
                        <OptionsMenu
                          menuItems={getMenuItems(row)}
                          visible={true}
                        />
                      )}
                    </td>
                    <td>
                      {formData.enrolledSemester && (
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(row.uuid)}
                          onChange={(e) =>
                            handleSelectStudent(row.uuid, e.target.checked)
                          }
                          className="checkBox"
                        />
                      )}
                    </td>
                    {Array.isArray(row.displayData) &&
                      row.displayData.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    style={{ fontSize: "14px", borderBottom: "none" }}
                  >
                    <NoRecord />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {totalCount > 0 && (
            <div className="recordInfo">
              Showing {startRecord}–{endRecord} of {totalCount} results
            </div>
          )}

          <div className="paginationControls">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  prev < Math.ceil(totalCount / pageSize) ? prev + 1 : prev,
                )
              }
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
            >
              Next
            </button>
          </div>
        </div>
        <div className="tail">
          {selectedStudents.length > 0 && (
            <span onClick={handlePromoteClick} className="promoteButton">
              <span className="title"> Promote Selected Students</span>
            </span>
          )}
          {selectedStudents.length > 0 && (
            <span onClick={handleAssignClick} className="assignButton">
              <span className="title"> Assign Section </span>
            </span>
          )}
        </div>
        {isDialogOpen && (
          <div className="dialogOverlay">
            <div className="dialogBox">
              <select
                value={promotionSemester}
                onChange={(e) => setPromotionSemester(e.target.value)}
              >
                <option value="">Select Semester</option>
                {semester.map((s) => (
                  <option key={s.uuid} value={s.uuid}>
                    {s.semester}
                  </option>
                ))}
              </select>
              <div className="dialogActions">
                <button onClick={handleDialogClose} className="cancel">
                  Cancel
                </button>
                <button onClick={handleDialogSubmit} className="submit">
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
        {isAssignDialogOpen && (
          <div className="dialogOverlay">
            <div className="dialogBox">
              <select
                value={assignSection}
                onChange={(e) => setAssignSection(e.target.value)}
              >
                <option value="">Select Section</option>
                {sections.map((s) => (
                  <option key={s.uuid} value={s.id}>
                    {s.Section.name}
                  </option>
                ))}
              </select>
              <div className="dialogActions">
                <button onClick={handleAssignDialogClose} className="cancel">
                  Cancel
                </button>
                <button onClick={handleAssignSection} className="submit">
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
        <ToastContainer position="top-center" />
      </div>
    </div>
  );
};

export default StudentList;
